'use client';

import React from 'react';
import Link from 'next/link';
import { ProtectedAppImage } from '@/components/ProtectedImage/ProtectedAppImage';
import Header from '@/components/common/Header';
import Footer from '@/app/homepage/components/SiteFooter';
import { useAuth } from '@/contexts/AuthContext';
import { getAuthRedirectUrl } from '@/lib/redirectUtils';

const Check = ({ className = 'w-5 h-5' }: { className?: string }) => (
  <svg className={className} fill="currentColor" viewBox="0 0 20 20">
    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
  </svg>
);

export default function FloorPlansService() {
  const { isAuthenticated } = useAuth();
  const signupUrl = getAuthRedirectUrl(isAuthenticated);

  const benefits = [
    { metric: '1 in 5', label: 'Buyers Lost', description: 'Buyers would ignore a property listing without a floor plan' },
    { metric: '93%', label: 'More Time', description: 'Of buyers spend more time on listings with floor plans' },
    { metric: '30%', label: 'More Leads', description: 'Floor plans generate 30% more interest than properties without' },
    { metric: '24 Hrs', label: 'Fast Delivery', description: 'Quick turnaround on all floor plan types' },
  ];

  const whyFloorPlans = [
    { title: 'Increase Buyer Interest', description: "1 in 5 buyers would ignore a property listing completely if it didn't include a floor plan.", icon: '👥' },
    { title: 'Allow Buyer Visualisation', description: 'Buyers want to see the property layout, to visualize the flow of the rooms, how they fit in relation to other rooms and picture their furniture inside the home.', icon: '🏠' },
    { title: 'Increase Time Spent on Your Listing', description: '93% of buyers say they are more likely to spend time looking at a property with a floor plan.', icon: '⏱️' },
    { title: 'Generate 30% More Leads', description: 'Floor plans have been shown to generate 30% more interest than properties without one.', icon: '📈' },
  ];

  const floorPlanTypes = [
    { title: '2D Black & White', price: '$24.00', turnaround: '4–12 Hours', description: "If you're looking for the no-nonsense, straight-up floor plan to clearly showcase the home's layout, then the 2D black and white floor plan is the perfect option. Clear, simple and easy to use.", icon: '📐', features: ['Clear layout visualization', 'Simple and professional', 'Perfect for standard listings', 'Fast turnaround'], popular: false },
    { title: '2D Full Color', price: '$28.00', turnaround: '4–12 Hours', description: 'Add an extra element of visual interest with a full color 2D floor plan. Different types of rooms can be color coded to showcase the layout of the home.', icon: '🎨', features: ['Color-coded rooms', 'Enhanced visual appeal', 'Easy room identification', 'Professional presentation'], popular: false },
    { title: '2D Textured', price: '$28.00', turnaround: '4–12 Hours', description: "Use a 2D textured floor plan to showcase the home's layout plus features like textured flooring such as carpets, tiles and decking, as well as furniture, providing much detail to potential buyers.", icon: '🪑', features: ['Textured flooring details', 'Furniture placement', 'Material visualization', 'Enhanced detail'], popular: false },
    { title: 'Floor and Site Plan Combination', price: 'From $32.00', turnaround: '4–12 Hours', description: 'Give potential buyers an overall picture of property and land combined. Great to show off external features such as landscaping or pools, large blocks or multiple dwellings.', icon: '🏡', features: ['Property + land layout', 'External features shown', 'Perfect for large blocks', 'Multiple dwellings'], popular: false },
    { title: '3D Full Color', price: '$32.00 per story', turnaround: '48 Hours', description: 'Almost as good as walking through the property, a 3D full color floor plan shows the home in greater detail. From flooring transitions to which way a door swings, it helps attract top quality buyers.', icon: '🏗️', features: ['Realistic 3D visualization', 'Door swing directions', 'Flooring transitions', 'Premium presentation'], popular: true },
    { title: 'Tailored 3D Floor Plans', price: 'From $160.00', turnaround: 'Custom', description: "Looking for something more bespoke? Our Tailored 3D Floor Plans let you fully customize property layouts, showcasing unique finishes, fixtures, and details. Ideal for new builds, multi-family residences, and luxury homes.", icon: '✨', features: ['Fully customizable', 'Luxury property focus', 'Unique finishes shown', 'Exceptional detail'], popular: false },
  ];

  const customTemplate = {
    title: 'Custom Floor Plan Templates',
    price: '$64.00',
    description: "Keep your agency's branding consistent and stand out by creating a custom floor plan to support your property listings. We'll create a branded template (one-off cost of US$64.00) that ensures uniform output every time, from logo placement to wall dimensions, furniture and fixtures. After that, you only pay for the style of floor plan selected.",
    icon: '🎯',
  };

  const beforeAfterExamples = [
    { before: '/images/gallery/2fpbf.webp?auto=compress&cs=tinysrgb&w=600', after: '/images/gallery/2fpaf.webp?auto=compress&cs=tinysrgb&w=600', title: '2D Black & White Floor Plan', turnaround: '4–12 Hours' },
    { before: '/images/gallery/1fpbf.webp?auto=compress&cs=tinysrgb&w=600', after: '/images/gallery/1fpaf.webp?auto=compress&cs=tinysrgb&w=600', title: '2D Full Color Floor Plan', turnaround: '4–12 Hours' },
    { before: '/images/gallery/3fpbf.webp?auto=compress&cs=tinysrgb&w=600', after: '/images/gallery/3fpaf.webp?auto=compress&cs=tinysrgb&w=600', title: '3D Full Color Floor Plan', turnaround: '48 Hours' },
  ];

  const pricingTiers = [
    { name: '2D Plans', price: '$24', unit: '-$28', tag: 'Perfect for standard listings', features: ['Black & White or Color', '4–12 hour turnaround', 'Clear layout visualization', 'Professional quality'], popular: false, cta: 'Get Started' },
    { name: '3D Full Color', price: '$32', unit: '/story', tag: 'Premium visualization', features: ['Realistic 3D rendering', '48-hour turnaround', 'Door swing directions', 'Premium presentation'], popular: true, cta: 'Start Project' },
    { name: 'Tailored 3D', price: '$160+', unit: '', tag: 'For luxury properties', features: ['Fully customizable', 'Unique finishes shown', 'Exceptional detail', 'Perfect for new builds'], popular: false, cta: 'Contact Sales' },
  ];

  const dotGrid = `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`;

  const Badge = ({ children }: { children: React.ReactNode }) => (
    <span className="inline-flex items-center bg-white/[0.04] border border-white/[0.12] text-purple-200 px-6 py-2 rounded-full text-sm font-bold">
      {children}
    </span>
  );

  return (
    <div className="bg-[#0B0D17] text-white">
      <Header />

      {/* ===================== HERO ===================== */}
      <section className="relative overflow-hidden bg-[#0B0D17] border-b border-white/[0.06]">
        <div className="absolute inset-0">
          <div className="absolute top-1/4 left-1/4 w-[1000px] h-[1000px] bg-purple-600/10 rounded-full blur-[160px] animate-glow-breathe" />
          <div className="absolute bottom-1/4 right-1/4 w-[800px] h-[800px] bg-blue-600/5 rounded-full blur-[140px] animate-glow-breathe-delayed" />
          <div className="absolute inset-0 opacity-[0.025]" style={{ backgroundImage: dotGrid }} />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12 py-20 lg:py-28">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-8">
              <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/[0.04] border border-white/[0.12] backdrop-blur-sm text-purple-200 text-xs font-medium">
                <span className="text-sm">📐</span>
                <span>Starting at $24.00</span>
              </div>

              <div className="space-y-6">
                <h1 className="text-5xl lg:text-7xl font-bold leading-[1.05] tracking-tight">
                  Floor plan{' '}
                  <span className="bg-gradient-to-r from-purple-400 via-purple-300 to-blue-400 bg-clip-text text-transparent">
                    redraws.
                  </span>
                </h1>
                <p className="text-xl text-gray-400 leading-relaxed">
                  Professional floor plans from $24.00 to $32.00 with 4–12 hour turnaround. Help buyers visualize the
                  property layout and generate 30% more leads.
                </p>
              </div>

              <div className="flex flex-col sm:flex-row gap-4">
                <Link href={signupUrl}>
                  <button className="group w-full sm:w-auto px-9 py-4 bg-gradient-to-r from-purple-600 to-purple-500 hover:from-purple-500 hover:to-purple-400 text-white rounded-xl font-bold text-lg transition-all duration-300 flex items-center justify-center gap-2 shadow-xl shadow-purple-500/30 hover:shadow-purple-500/50 hover:-translate-y-0.5">
                    Get Started Now
                    <svg className="w-5 h-5 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                    </svg>
                  </button>
                </Link>
                <Link href="#types">
                  <button className="w-full sm:w-auto px-9 py-4 bg-white/[0.03] hover:bg-white/[0.08] backdrop-blur-sm border border-white/[0.15] hover:border-white/30 text-white rounded-xl font-bold text-lg transition-all duration-300">
                    View Floor Plan Types
                  </button>
                </Link>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 pt-4">
                {benefits.map((benefit, i) => (
                  <div key={i} className="text-center bg-white/[0.03] border border-white/[0.08] rounded-xl p-4 hover:border-purple-500/30 transition-all duration-300">
                    <div className="text-2xl lg:text-3xl font-bold bg-gradient-to-r from-purple-400 to-blue-400 bg-clip-text text-transparent mb-1">{benefit.metric}</div>
                    <div className="text-xs font-semibold text-white mb-1">{benefit.label}</div>
                    <div className="text-xs text-gray-500 leading-tight">{benefit.description}</div>
                  </div>
                ))}
              </div>
            </div>

            <div className="relative">
              <div className="absolute -inset-4 bg-purple-600/15 rounded-3xl blur-[80px] -z-10" />
              <div className="relative h-[450px] lg:h-[550px] rounded-2xl overflow-hidden shadow-2xl border-2 border-white/[0.12]">
                <ProtectedAppImage
                  src="/images/gallery/5fp.webp?auto=compress&cs=tinysrgb&w=1200"
                  alt="Professional floor plan"
                  className="w-full h-full object-cover"
                />
                <div className="absolute top-6 right-6 bg-gradient-to-r from-purple-600 to-purple-500 text-white px-5 py-3 rounded-lg font-bold text-sm shadow-lg shadow-purple-500/30">
                  PROFESSIONAL QUALITY
                </div>
                <div className="absolute bottom-6 left-6 bg-black/60 backdrop-blur-sm border border-white/10 text-white px-5 py-3 rounded-lg font-bold text-sm">
                  4–12 HOUR TURNAROUND
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===================== WHY FLOOR PLANS ===================== */}
      <section className="relative py-20 lg:py-28 px-6 lg:px-12">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16 max-w-3xl mx-auto">
            <h2 className="text-4xl lg:text-5xl font-bold leading-tight tracking-tight mb-4">Why floor plans matter</h2>
            <p className="text-lg text-gray-400 leading-relaxed">
              Floor plans aren&apos;t just nice to have — they&apos;re essential for modern property marketing and driving
              real buyer engagement.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {whyFloorPlans.map((item, index) => (
              <div key={index} className="group text-center bg-[#13151F]/80 backdrop-blur-sm border border-white/[0.08] rounded-2xl p-8 hover:border-purple-500/30 hover:-translate-y-1 transition-all duration-300">
                <div className="w-16 h-16 mx-auto rounded-2xl bg-gradient-to-br from-purple-500/20 to-blue-500/10 border border-purple-500/20 flex items-center justify-center text-3xl mb-5 group-hover:scale-110 transition-transform">
                  {item.icon}
                </div>
                <h3 className="text-lg font-bold text-white mb-3">{item.title}</h3>
                <p className="text-gray-400 text-sm leading-relaxed">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===================== FLOOR PLAN TYPES ===================== */}
      <section id="types" className="relative py-20 lg:py-28 px-6 lg:px-12 bg-white/[0.015] border-y border-white/[0.06] scroll-mt-20">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16 max-w-3xl mx-auto">
            <div className="mb-6"><Badge>CHOOSE YOUR FLOOR PLAN TYPE</Badge></div>
            <h2 className="text-4xl lg:text-5xl font-bold leading-tight tracking-tight mb-4">Floor plan options</h2>
            <p className="text-lg text-gray-400 leading-relaxed">
              From simple 2D black and white to stunning 3D full color floor plans, we have the perfect option for
              every property and budget.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-10 items-start">
            {floorPlanTypes.map((plan, index) => (
              <div
                key={index}
                className={
                  plan.popular
                    ? 'relative bg-gradient-to-b from-purple-600/20 to-[#13151F]/90 backdrop-blur-sm border-2 border-purple-500/40 rounded-2xl p-8 shadow-[0_30px_80px_rgba(124,58,237,0.25)] lg:scale-105'
                    : 'relative bg-[#13151F]/80 backdrop-blur-sm border border-white/[0.08] rounded-2xl p-8 hover:border-purple-500/30 transition-all duration-300'
                }
              >
                {plan.popular && (
                  <div className="absolute -top-3.5 left-1/2 -translate-x-1/2 bg-gradient-to-r from-purple-500 to-blue-500 text-white px-5 py-1.5 rounded-full text-xs font-bold shadow-lg">
                    MOST POPULAR
                  </div>
                )}
                <div className="text-center mb-6">
                  <div className="text-4xl mb-4">{plan.icon}</div>
                  <h3 className="text-xl font-bold text-white mb-3">{plan.title}</h3>
                  <div className="flex items-baseline justify-center gap-2 mb-2">
                    <span className={plan.popular ? 'text-3xl font-bold text-white' : 'text-3xl font-bold bg-gradient-to-r from-purple-400 to-blue-400 bg-clip-text text-transparent'}>{plan.price}</span>
                  </div>
                  <div className="flex items-center justify-center gap-2 text-sm text-gray-400 mb-4">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span>{plan.turnaround}</span>
                  </div>
                </div>
                <p className={`text-sm leading-relaxed mb-6 ${plan.popular ? 'text-gray-300' : 'text-gray-400'}`}>{plan.description}</p>
                <ul className="space-y-3 mb-6">
                  {plan.features.map((feature, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <Check className={`w-4 h-4 flex-shrink-0 mt-0.5 ${plan.popular ? 'text-purple-300' : 'text-purple-400'}`} />
                      <span className={`text-sm ${plan.popular ? 'text-white' : 'text-gray-300'}`}>{feature}</span>
                    </li>
                  ))}
                </ul>
                <Link href={signupUrl}>
                  <button
                    className={
                      plan.popular
                        ? 'w-full py-3 bg-gradient-to-r from-purple-600 to-purple-500 hover:from-purple-500 hover:to-purple-400 text-white rounded-xl font-bold transition-all shadow-lg shadow-purple-500/30 hover:-translate-y-0.5'
                        : 'w-full py-3 bg-white/[0.05] border border-white/[0.12] text-white rounded-xl font-bold hover:bg-white/[0.1] transition-all'
                    }
                  >
                    Get Started
                  </button>
                </Link>
              </div>
            ))}
          </div>

          {/* Custom Template */}
          <div className="max-w-4xl mx-auto">
            <div className="p-8 rounded-2xl bg-gradient-to-r from-purple-500/[0.08] to-blue-500/[0.05] border border-purple-500/20">
              <div className="flex items-start gap-6">
                <div className="text-5xl">{customTemplate.icon}</div>
                <div className="flex-1">
                  <div className="flex flex-wrap items-center gap-3 mb-3">
                    <h3 className="text-2xl font-bold text-white">{customTemplate.title}</h3>
                    <span className="text-2xl font-bold bg-gradient-to-r from-purple-400 to-blue-400 bg-clip-text text-transparent">{customTemplate.price}</span>
                    <span className="text-xs font-bold text-purple-200 bg-white/[0.06] border border-white/10 px-3 py-1 rounded-full">ONE-TIME FEE</span>
                  </div>
                  <p className="text-gray-400 leading-relaxed">{customTemplate.description}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===================== BEFORE / AFTER ===================== */}
      <section className="relative py-20 lg:py-28 px-6 lg:px-12">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16 max-w-2xl mx-auto">
            <h2 className="text-4xl lg:text-5xl font-bold leading-tight tracking-tight mb-4">See our work</h2>
            <p className="text-lg text-gray-400 leading-relaxed">
              Real examples of our professional floor plan redraws showcasing clarity and attention to detail.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {beforeAfterExamples.map((example, index) => (
              <div key={index} className="bg-[#13151F]/80 backdrop-blur-sm rounded-2xl overflow-hidden border border-white/[0.08] hover:border-purple-500/30 transition-all duration-300">
                <div className="grid grid-cols-2 gap-0">
                  <div className="relative h-48">
                    <ProtectedAppImage src={example.before} alt={`${example.title} - Before`} className="w-full h-full object-cover" />
                    <div className="absolute top-3 left-3 bg-black/60 backdrop-blur-sm border border-white/10 text-white px-3 py-1 text-xs font-bold rounded">BEFORE</div>
                  </div>
                  <div className="relative h-48">
                    <ProtectedAppImage src={example.after} alt={`${example.title} - After`} className="w-full h-full object-cover" />
                    <div className="absolute top-3 right-3 bg-gradient-to-r from-purple-600 to-purple-500 text-white px-3 py-1 text-xs font-bold rounded shadow-lg shadow-purple-500/30">AFTER</div>
                  </div>
                </div>
                <div className="p-6">
                  <h3 className="font-bold text-lg text-white mb-2">{example.title}</h3>
                  <div className="flex items-center gap-2 text-sm text-gray-400">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span>{example.turnaround}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===================== GET STARTED ===================== */}
      <section className="relative py-20 lg:py-28 px-6 lg:px-12 bg-white/[0.015] border-y border-white/[0.06]">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl lg:text-5xl font-bold leading-tight tracking-tight mb-5">Ready to get started?</h2>
          <p className="text-lg text-gray-400 leading-relaxed mb-10 max-w-2xl mx-auto">
            Sign up now and get professional floor plans that help buyers visualize your property. No credit card
            required to create an account.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link href={signupUrl}>
              <button className="px-9 py-4 bg-gradient-to-r from-purple-600 to-purple-500 hover:from-purple-500 hover:to-purple-400 text-white rounded-xl font-bold text-lg transition-all duration-300 shadow-xl shadow-purple-500/30 hover:-translate-y-0.5">
                Create Free Account
              </button>
            </Link>
            <Link href="/auth/signin">
              <button className="px-9 py-4 bg-white/[0.03] hover:bg-white/[0.08] backdrop-blur-sm border border-white/[0.15] hover:border-white/30 text-white rounded-xl font-bold text-lg transition-all duration-300">
                Sign In
              </button>
            </Link>
          </div>
          <p className="text-sm text-gray-500 mt-6">Already have an account? Sign in to start your project</p>
        </div>
      </section>

      {/* ===================== PRICING ===================== */}
      <section className="relative py-20 lg:py-28 px-6 lg:px-12">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16 max-w-2xl mx-auto">
            <h2 className="text-4xl lg:text-5xl font-bold leading-tight tracking-tight mb-4">Transparent, affordable pricing</h2>
            <p className="text-lg text-gray-400 leading-relaxed">
              Professional floor plans at competitive prices. No hidden fees, no subscriptions, no surprises.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-6 max-w-6xl mx-auto items-start">
            {pricingTiers.map((tier) => (
              <div
                key={tier.name}
                className={
                  tier.popular
                    ? 'relative bg-gradient-to-b from-purple-600/20 to-[#13151F]/90 backdrop-blur-sm border-2 border-purple-500/40 rounded-2xl p-8 shadow-[0_30px_80px_rgba(124,58,237,0.25)] md:scale-105'
                    : 'bg-[#13151F]/80 backdrop-blur-sm border border-white/[0.08] rounded-2xl p-8 hover:border-purple-500/30 transition-all duration-300'
                }
              >
                {tier.popular && (
                  <div className="absolute -top-3.5 left-1/2 -translate-x-1/2 bg-gradient-to-r from-purple-500 to-blue-500 text-white px-5 py-1.5 rounded-full text-xs font-bold shadow-lg">
                    MOST POPULAR
                  </div>
                )}
                <div className="text-center mb-6">
                  <h3 className="text-2xl font-bold text-white mb-3">{tier.name}</h3>
                  <div className="flex items-baseline justify-center gap-2">
                    <span className={tier.popular ? 'text-5xl font-bold text-white' : 'text-5xl font-bold bg-gradient-to-r from-purple-400 to-blue-400 bg-clip-text text-transparent'}>{tier.price}</span>
                    {tier.unit && <span className={tier.popular ? 'text-gray-300' : 'text-gray-500'}>{tier.unit}</span>}
                  </div>
                  <p className={`text-sm mt-3 ${tier.popular ? 'text-gray-300' : 'text-gray-500'}`}>{tier.tag}</p>
                </div>
                <ul className="space-y-3 mb-8">
                  {tier.features.map((f) => (
                    <li key={f} className="flex items-start gap-3">
                      <Check className={`w-5 h-5 flex-shrink-0 mt-0.5 ${tier.popular ? 'text-purple-300' : 'text-purple-400'}`} />
                      <span className={tier.popular ? 'text-white' : 'text-gray-400'}>{f}</span>
                    </li>
                  ))}
                </ul>
                {tier.cta === 'Contact Sales' ? (
                  <Link href="/contact">
                    <button className="w-full py-3.5 bg-white/[0.05] border border-white/[0.12] text-white rounded-xl font-bold hover:bg-white/[0.1] transition-all">Contact Sales</button>
                  </Link>
                ) : (
                  <Link href={signupUrl}>
                    <button
                      className={
                        tier.popular
                          ? 'w-full py-3.5 bg-gradient-to-r from-purple-600 to-purple-500 hover:from-purple-500 hover:to-purple-400 text-white rounded-xl font-bold transition-all shadow-lg shadow-purple-500/30 hover:-translate-y-0.5'
                          : 'w-full py-3.5 bg-white/[0.05] border border-white/[0.12] text-white rounded-xl font-bold hover:bg-white/[0.1] transition-all'
                      }
                    >
                      {tier.cta}
                    </button>
                  </Link>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===================== QUALITY GUARANTEE ===================== */}
      <section className="relative py-20 lg:py-28 px-6 lg:px-12 bg-white/[0.015] border-y border-white/[0.06]">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-12 items-center">
          <div className="space-y-6">
            <h2 className="text-4xl lg:text-5xl font-bold leading-tight tracking-tight">
              Quality{' '}
              <span className="bg-gradient-to-r from-purple-400 to-blue-400 bg-clip-text text-transparent">guaranteed.</span>
            </h2>
            <p className="text-lg text-gray-400 leading-relaxed">
              We stand behind our work with a 100% satisfaction guarantee. If you&apos;re not happy, we&apos;ll make it
              right or refund your money.
            </p>
            <ul className="space-y-4">
              {[
                '4–12 hour turnaround time',
                'Unlimited free revisions',
                'Expert drafters with 10+ years experience',
                'Accurate measurements and dimensions',
                '100% satisfaction guarantee',
                'Secure file handling & privacy',
              ].map((item, i) => (
                <li key={i} className="flex items-start gap-3">
                  <Check className="w-6 h-6 text-purple-400 flex-shrink-0" />
                  <span className="text-gray-300 text-lg">{item}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="relative">
            <div className="absolute -inset-4 bg-purple-600/15 rounded-3xl blur-[80px] -z-10" />
            <div className="relative h-[500px] rounded-2xl overflow-hidden shadow-2xl border-2 border-white/[0.12]">
              <ProtectedAppImage src="/images/gallery/4fp.webp?auto=compress&cs=tinysrgb&w=1200" alt="Quality guarantee" className="w-full h-full object-cover" />
            </div>
          </div>
        </div>
      </section>

      {/* ===================== FINAL CTA ===================== */}
      <section className="relative py-20 lg:py-28 px-6 lg:px-12 overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[900px] h-[900px] bg-purple-600/10 rounded-full blur-[160px] animate-glow-breathe" />
        </div>
        <div className="relative z-10 max-w-4xl mx-auto text-center">
          <div className="relative bg-[#13151F]/95 backdrop-blur-2xl rounded-3xl border-2 border-white/[0.12] shadow-[0_30px_80px_rgba(0,0,0,0.6)] p-12 lg:p-16">
            <div className="absolute -inset-4 bg-purple-600/15 rounded-3xl blur-[80px] -z-10" />
            <h2 className="text-4xl lg:text-5xl font-bold leading-tight tracking-tight mb-5">
              Ready to create your{' '}
              <span className="bg-gradient-to-r from-purple-400 to-blue-400 bg-clip-text text-transparent">floor plans?</span>
            </h2>
            <p className="text-lg text-gray-400 leading-relaxed mb-10 max-w-2xl mx-auto">
              Join thousands of real estate professionals who trust Shashio for professional floor plan redraws. Get
              started today and help buyers visualize your property.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link href={signupUrl}>
                <button className="px-10 py-4 bg-gradient-to-r from-purple-600 to-purple-500 hover:from-purple-500 hover:to-purple-400 text-white font-bold text-lg rounded-xl transition-all duration-300 shadow-xl shadow-purple-500/30 hover:-translate-y-0.5">
                  Start Your Project
                </button>
              </Link>
              <Link href="/homepage#pricing">
                <button className="px-10 py-4 bg-white/[0.03] hover:bg-white/[0.08] backdrop-blur-sm text-white font-bold text-lg rounded-xl transition-all duration-300 border border-white/[0.15] hover:border-white/30">
                  View Pricing
                </button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      <Footer />

      {/* Animations */}
      <style jsx global>{`
        @keyframes glow-breathe {
          0%, 100% { opacity: 0.1; transform: scale(1); }
          50% { opacity: 0.15; transform: scale(1.05); }
        }
        @keyframes glow-breathe-delayed {
          0%, 100% { opacity: 0.05; transform: scale(1); }
          50% { opacity: 0.08; transform: scale(1.03); }
        }
        .animate-glow-breathe { animation: glow-breathe 8s ease-in-out infinite; }
        .animate-glow-breathe-delayed { animation: glow-breathe-delayed 10s ease-in-out infinite; animation-delay: 2s; }
      `}</style>
    </div>
  );
}
